import {memo, useCallback, useRef, useState} from 'react'
import {XIcon} from '@primer/octicons-react'
import {Button, CounterLabel, IconButton, SegmentedControl} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  DockedPanel,
  useDockedPanelActions,
  useDockedPanelFocusRefs,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../../contexts/DockedPanelContext'
import {useUnifiedBatchActions, useUnifiedBatchCount} from '../../stores/layout-selectors'
import {Panel} from './Panel'
import {getPanelHeading} from './panel-registry'
import {restorePanelFocusAfterClose} from './panel-focus'
import {MANAGE_BATCH_COMMENTS_HEADING_ID, ManageBatchCommentsList} from './manage-batch/ManageBatchCommentsList'
import {useManageBatchTelemetry} from './manage-batch/manage-batch-telemetry'
import styles from './ManageBatchPanel.module.css'

export type ManageBatchTab = 'commit' | 'fix'

const TAB_DEFS: Array<{value: ManageBatchTab; label: string}> = [
  {value: 'commit', label: 'Commit suggestions'},
  {value: 'fix', label: 'Fix with Copilot'},
]

const TAB_VALUES = TAB_DEFS.map(({value}) => value)

/**
 * Manage batch panel shell.
 *
 * Hosts the docked-panel surface for the unified batch experience. This file
 * delivers the structural pieces only:
 *  - Tab header (Commit suggestions | Fix with Copilot).
 *  - Shared `Comments(N)` list with per-row remove.
 *  - Footer slot framework (Discard batch / Cancel / per-tab primary CTA slot).
 *  - Empty state when the batch is empty.
 *
 * The tab-body content slots are intentionally empty placeholders for the
 * tab-content sub-issues to fill:
 *  - copilot-discovery-team#798 — Commit suggestions tab content
 *  - copilot-discovery-team#799 — Fix with Copilot tab content
 *  - copilot-discovery-team#800 — Default-tab logic + disallowed-tab warning banner
 *  - copilot-discovery-team#801 — Outdated suggestion handling
 *
 * Gated behind `unified_batch_pr_comments`. Registered in `panel-registry.tsx`
 * so it joins the enabled-panel set (and the panel-selector dropdown) when
 * the flag is on. Mounted by `DockedSidePanelContent` via `panel-render-map`
 * when the panel is active and the viewport is non-compact; falls back to a
 * Dialog in compact mode.
 */
export const ManageBatchPanel = memo(function ManageBatchPanelImpl() {
  const isFlagEnabled = isFeatureEnabled('unified_batch_pr_comments')
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.ManageBatchReviewComments)
  const isCompact = useIsDockedPanelCompact()
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const {returnFocusRef} = useDockedPanelFocusRefs()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const [activeTab, setActiveTab] = useState<ManageBatchTab>('commit')

  if (!isFlagEnabled || !isDockedPanelOpen) return null

  if (!isCompact) {
    return (
      <Panel panelName={DockedPanel.ManageBatchReviewComments}>
        <Panel.Header>
          <ManageBatchTabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
        </Panel.Header>
        <Panel.Body className="d-flex flex-column tmp-p-3">
          <ManageBatchBody activeTab={activeTab} />
        </Panel.Body>
        <Panel.Footer>
          <ManageBatchFooter activeTab={activeTab} />
        </Panel.Footer>
      </Panel>
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={returnFocusRef}
      onClose={closeManageBatchPanel}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>{getPanelHeading(DockedPanel.ManageBatchReviewComments)}</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close manage batch panel"
              icon={XIcon}
              variant="invisible"
              onClick={closeManageBatchPanel}
            />
          </div>
          <ManageBatchTabsHeader activeTab={activeTab} setActiveTab={setActiveTab} />
        </Dialog.Header>
      )}
      renderFooter={() => (
        <Dialog.Footer>
          <ManageBatchFooter activeTab={activeTab} />
        </Dialog.Footer>
      )}
    >
      <div className="d-flex flex-column tmp-p-3">
        <ManageBatchBody activeTab={activeTab} />
      </div>
    </Dialog>
  )
})

function ManageBatchTabsHeader({
  activeTab,
  setActiveTab,
}: {
  activeTab: ManageBatchTab
  setActiveTab: (tab: ManageBatchTab) => void
}) {
  const {onTabSelected} = useManageBatchTelemetry()

  const handleChange = useCallback(
    (selectedIndex: number) => {
      const next = TAB_VALUES[selectedIndex] ?? 'commit'
      setActiveTab(next)
      onTabSelected(next)
    },
    [setActiveTab, onTabSelected],
  )

  return (
    <SegmentedControl aria-label="Manage batch action mode" fullWidth onChange={handleChange} className={styles.tabs}>
      {TAB_DEFS.map(({value, label}) => (
        <SegmentedControl.Button key={value} selected={activeTab === value} data-testid={`manage-batch-tab-${value}`}>
          {label}
        </SegmentedControl.Button>
      ))}
    </SegmentedControl>
  )
}

function ManageBatchBody({activeTab}: {activeTab: ManageBatchTab}) {
  const count = useUnifiedBatchCount()

  return (
    <>
      {/* Tab-content slot: copilot-discovery-team#798 fills the Commit tab, copilot-discovery-team#799 fills the Fix tab. */}
      <div data-testid={`manage-batch-tab-body-${activeTab}`} />
      <div>
        <h3 className={styles.commentsHeading} id={MANAGE_BATCH_COMMENTS_HEADING_ID} tabIndex={-1}>
          Batched comments <CounterLabel className="ml-1">{count}</CounterLabel>
        </h3>
        <ManageBatchCommentsList />
      </div>
    </>
  )
}

function ManageBatchFooter({activeTab}: {activeTab: ManageBatchTab}) {
  const closeManageBatchPanel = useCloseManageBatchPanel()
  const {clearBatch} = useUnifiedBatchActions()
  const {onPanelClose} = useManageBatchTelemetry()

  const handleDiscard = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
    clearBatch()
  }, [clearBatch, closeManageBatchPanel, onPanelClose])

  const handleCancel = useCallback(() => {
    onPanelClose()
    closeManageBatchPanel()
  }, [closeManageBatchPanel, onPanelClose])

  return (
    <div className="d-flex flex-row flex-justify-between flex-items-center">
      <Button variant="danger" onClick={handleDiscard}>
        Discard batch
      </Button>
      <div className="d-flex gap-2">
        <Button variant="default" onClick={handleCancel}>
          Cancel
        </Button>
        {/* Primary CTA slot: copilot-discovery-team#798 renders `Commit suggestions`; copilot-discovery-team#799 renders `Fix and commit ▾`. */}
        <div data-testid={`manage-batch-primary-cta-slot-${activeTab}`} />
      </div>
    </div>
  )
}

function useCloseManageBatchPanel() {
  const {closePanel} = useDockedPanelActions()
  const {returnFocusRef} = useDockedPanelFocusRefs()

  return useCallback(() => {
    restorePanelFocusAfterClose(returnFocusRef.current)
    closePanel()
  }, [closePanel, returnFocusRef])
}
